import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/api';

const InspectionList = () => {
  const [houseData, setHouseData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedHouses, setExpandedHouses] = useState(new Set());
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [inputValues, setInputValues] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setCurrentUser(JSON.parse(userData));
    }
    fetchHouseData();
  }, []);

  const handleInputChange = (inspectionId, field, value) => {
    setInputValues(prev => ({
      ...prev,
      [`${inspectionId}_${field}`]: value
    }));
  };

  const handleSubmit = async (inspection) => {
    const snaValue = inputValues[`${inspection.id}_sna`] || '';
    const ftoValue = inputValues[`${inspection.id}_fto`] || '';
    
    if (!snaValue.trim() && !ftoValue.trim()) {
      alert('Please enter at least one value (SNA or FTO) before submitting.');
      return;
    }

    // Validate that SNA + FTO doesn't exceed total_amount_received
    const snaAmount = parseFloat(snaValue.trim()) || 0;
    const ftoAmount = parseFloat(ftoValue.trim()) || 0;
    const totalEntered = snaAmount + ftoAmount;
    const totalAmountReceived = inspection.stage_data?.total_amount_received || 0;

    if (totalEntered > totalAmountReceived) {
      alert(`Error: The total of SNA (₹${snaAmount.toLocaleString()}) and FTO (₹${ftoAmount.toLocaleString()}) = ₹${totalEntered.toLocaleString()} exceeds the Total Amount Received (₹${totalAmountReceived.toLocaleString()}).\n\nPlease enter values that don't exceed ₹${totalAmountReceived.toLocaleString()}.`);
      return;
    }
    
    setSubmitLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/house/inspection/${inspection.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sna: snaValue.trim(),
          fto: ftoValue.trim()
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        alert(`Data successfully updated for inspection ${inspection.id}:\nSNA: ₹${snaAmount.toLocaleString()}\nFTO: ₹${ftoAmount.toLocaleString()}\nTotal: ₹${totalEntered.toLocaleString()}`);
        // Clear the input fields after successful submission
        setInputValues(prev => ({
          ...prev,
          [`${inspection.id}_sna`]: '',
          [`${inspection.id}_fto`]: ''
        }));
        // Refresh inspection details
        fetchInspectionDetails(inspection.id);
      } else {
        alert(`Error: ${data.message || 'Failed to update inspection data'}`);
      }
    } catch (err) {
      alert('Network error. Please check your connection and try again.');
      console.error('Submit error:', err);
    } finally {
      setSubmitLoading(false);
    }
  };

  const fetchHouseData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/list/ins-smr`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        setHouseData(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch house data');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchInspectionDetails = async (inspectionId) => {
    setDetailsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/project/house/inspect/${inspectionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        setSelectedInspection(data.data[0]);
      } else {
        alert(data.message || 'Failed to fetch inspection details');
      }
    } catch (err) {
      alert('Failed to load inspection details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const toggleHouseExpansion = (houseId) => {
    const newExpanded = new Set(expandedHouses);
    if (newExpanded.has(houseId)) {
      newExpanded.delete(houseId);
    } else {
      newExpanded.add(houseId);
    }
    setExpandedHouses(newExpanded);
  };

  const getStatusBadge = (stage) => {
    if (!stage) return null;
    
    const getStageStyle = (stageName) => {
      const name = stageName.toLowerCase();
      
      if (name.includes('basement')) {
        return { bg: 'bg-orange-100', text: 'text-orange-800' };
      } else if (name.includes('lintel') || name.includes('lintal')) {
        return { bg: 'bg-yellow-100', text: 'text-yellow-800' };
      } else if (name.includes('roof')) {
        return { bg: 'bg-blue-100', text: 'text-blue-800' };
      } else if (name.includes('plaster')) {
        return { bg: 'bg-purple-100', text: 'text-purple-800' };
      } else if (name.includes('completion') || name.includes('whitewash')) {
        return { bg: 'bg-green-100', text: 'text-green-800' };
      } else {
        return { bg: 'bg-gray-100', text: 'text-gray-800' };
      }
    };
    
    const style = getStageStyle(stage);
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${style.bg} ${style.text}`}>
        {stage}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isAdmin = currentUser?.role === 'admin';

  // Filter houses based on search query
  const filteredHouseData = houseData.filter(house => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const houseId = house.h_id?.toLowerCase() || '';
    const beneficiary = house.beneficiary?.toLowerCase() || '';
    
    return houseId.includes(query) || beneficiary.includes(query);
  });

  // If showing inspection details view
  if (selectedInspection) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="w-full px-3 py-3 max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4 bg-white rounded-lg p-4 shadow-sm">
            <button
              onClick={() => setSelectedInspection(null)}
              className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to Houses</span>
            </button>
            
            <div className="text-center">
              <h1 className="text-lg font-bold text-gray-900">
                Inspection Details #{selectedInspection.id}
              </h1>
            </div>
            
            <div className="w-24"></div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h2 className="text-lg font-semibold mb-4">Inspection Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Inspection ID</label>
                <p className="text-sm text-gray-900">{selectedInspection.id}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Progress Stage</label>
                <div className="mt-1">{getStatusBadge(selectedInspection.progress?.stage)}</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Achieved Date</label>
                <p className="text-sm text-gray-900">{formatDate(selectedInspection.progress?.achieved)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Inspection Date</label>
                <p className="text-sm text-gray-900">{formatDate(selectedInspection.inspect)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Total Amount Received</label>
                <p className="text-sm font-medium text-green-600">₹{selectedInspection.stage_data?.total_amount_received?.toLocaleString()}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Financing Source</label>
                <p className="text-sm text-gray-900">{selectedInspection.stage_data?.financing_source || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Help Needed</label>
                <p className="text-sm text-gray-900">{selectedInspection.stage_data?.help_needed || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">GO50 Received</label>
                <p className="text-sm text-gray-900">{selectedInspection.stage_data?.go50_received || 'N/A'}</p>
              </div>

        <div>
                <label className="block text-sm font-medium text-gray-700">House Size</label>
                <p className="text-sm text-gray-900">{selectedInspection.house_size || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Construction Ongoing</label>
                <p className="text-sm text-gray-900">{selectedInspection.stage_data?.construction_ongoing || 'N/A'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <p className="text-sm text-gray-900">{selectedInspection.stage_data?.status || 'N/A'}</p>
              </div>

<div>
  <label className="block text-sm font-medium text-gray-700">Remarks</label>
  <p className="text-sm text-gray-900">{selectedInspection.remarks || 'N/A'}</p>
</div>

<div className="col-span-full">
  <label className="block text-sm font-medium text-gray-700 mb-2">Inspection Images</label>
  {selectedInspection.images && selectedInspection.images.length > 0 ? (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {selectedInspection.images.map((imagePath, index) => (
        <div key={index} className="relative group">
          <img
            src={`https://housing.vpmrd.in/uploads/inspection/${imagePath}`}
            alt={`Inspection ${index + 1}`}
            className="w-full h-48 object-cover rounded-lg border border-gray-300 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => window.open(`https://housing.vpmrd.in/uploads/inspection/${imagePath}`, '_blank')}
            onError={(e) => {
              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"%3E%3Crect fill="%23f0f0f0" width="100" height="100"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E%3C/svg%3E';
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity rounded-lg flex items-center justify-center">
            <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <p className="text-xs text-gray-500 mt-1 text-center">Image {index + 1}</p>
        </div>
      ))}
    </div>
  ) : (
    <p className="text-sm text-gray-500 italic">No images available for this inspection</p>
  )}
</div>

<div>
  <label className="block text-sm font-medium text-gray-700">SNA</label>
                <p className="text-sm font-medium text-blue-600">{selectedInspection.sna ? `₹${parseFloat(selectedInspection.sna).toLocaleString()}` : 'Not Available'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">FTO</label>
                <p className="text-sm font-medium text-blue-600">{selectedInspection.fto ? `₹${parseFloat(selectedInspection.fto).toLocaleString()}` : 'Not Available'}</p>
              </div>
            </div>

            {/* Materials Section */}
            {selectedInspection.stage_data?.materials && Object.keys(selectedInspection.stage_data.materials).length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-semibold mb-3 text-blue-800">Materials Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(selectedInspection.stage_data.materials).map(([stage, data]) => (
                    <div key={stage} className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-blue-700 mb-2">{stage}</h4>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>Provided:</span>
                          <span className={`px-2 py-1 rounded text-white ${
                            data.provided === 'yes' ? 'bg-green-500' : 
                            data.provided === 'no' ? 'bg-red-500' : 'bg-gray-400'
                          }`}>
                            {data.provided?.toUpperCase() || 'N/A'}
                          </span>
                        </div>
                        {data.cement_received && (
                          <div className="flex justify-between text-xs">
                            <span>Cement Received:</span>
                            <span className="font-medium">{data.cement_received}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* MGNREGS Section */}
            {selectedInspection.stage_data?.mgnregs && Object.keys(selectedInspection.stage_data.mgnregs).length > 0 && (
              <div className="mb-6">
                <h3 className="text-md font-semibold mb-3 text-green-800">MGNREGS Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(selectedInspection.stage_data.mgnregs).map(([stage, data]) => (
                    <div key={stage} className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-green-700 mb-2">{stage}</h4>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span>Received:</span>
                          <span className={`px-2 py-1 rounded text-white ${
                            data.received === 'yes' ? 'bg-green-500' : 
                            data.received === 'no' ? 'bg-red-500' : 'bg-gray-400'
                          }`}>
                            {data.received?.toUpperCase() || 'N/A'}
                          </span>
                        </div>
                        {data.amount && (
                          <div className="flex justify-between text-xs">
                            <span>Amount:</span>
                            <span className="font-medium text-green-600">₹{data.amount}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Total Amount Status section */}
            <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-4 mb-6">
              <h3 className="text-md font-semibold text-yellow-800 mb-2">Amount Status</h3>
              <div className="text-sm text-yellow-700">
                <p><strong>Total Amount Received:</strong> ₹{selectedInspection.stage_data?.total_amount_received?.toLocaleString()}</p>
                {!isAdmin && (!selectedInspection.sna && !selectedInspection.fto) && (
                  <p className="text-xs mt-1">Please update SNA and FTO information below. Total cannot exceed the amount received.</p>
                )}
              </div>
            </div>

            {/* Update Data section - Only for non-admin users and if SNA/FTO not present */}
            {!isAdmin && (!selectedInspection.sna && !selectedInspection.fto) && (
              <div className="border-t pt-4">
                <h3 className="text-md font-semibold mb-4">Update Data</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SNA</label>
                    <input
                      type="number"
                      placeholder="Enter SNA value"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={inputValues[`${selectedInspection.id}_sna`] || ''}
                      onChange={(e) => handleInputChange(selectedInspection.id, 'sna', e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">FTO</label>
                    <input
                      type="number"
                      placeholder="Enter FTO value"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={inputValues[`${selectedInspection.id}_fto`] || ''}
                      onChange={(e) => handleInputChange(selectedInspection.id, 'fto', e.target.value)}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> SNA + FTO total cannot exceed ₹{selectedInspection.stage_data?.total_amount_received?.toLocaleString()}
                  </p>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium px-6 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    onClick={() => handleSubmit(selectedInspection)}
                    disabled={submitLoading}
                  >
                    {submitLoading ? 'Submitting...' : 'Submit Data'}
                  </button>
                </div>
              </div>
            )}

            {/* Show message when SNA/FTO already exist */}
            {(selectedInspection.sna || selectedInspection.fto) && (
              <div className="border-t pt-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <h3 className="text-sm font-semibold text-green-800">SNA/FTO Data Already Available</h3>
                  </div>
                  <p className="text-sm text-green-700 mt-2">
                    This inspection already has SNA and/or FTO data recorded.
                    
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading houses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-xl font-semibold mb-4">Error</div>
          <p className="text-gray-600">{error}</p>
          <button onClick={fetchHouseData} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="w-full px-2 sm:px-4 py-4">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">House List</h1>
          <p className="text-gray-600">Click houses to expand inspections, click inspections for details</p>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search by House ID or Beneficiary name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 pl-10 pr-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <svg 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="mt-2 text-sm text-gray-600">
              Found {filteredHouseData.length} result{filteredHouseData.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">House</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beneficiary</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stage</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inspections</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHouseData.length > 0 ? (
                filteredHouseData.map((house) => (
                  <React.Fragment key={house.id}>
                    <tr 
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => toggleHouseExpansion(house.h_id)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <svg 
                            className={`w-4 h-4 mr-2 transition-transform ${
                              expandedHouses.has(house.h_id) ? 'rotate-90' : ''
                            }`} 
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{house.h_id}</div>
                            <div className="text-xs text-gray-500">{house.order_ref}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{house.beneficiary}</div>
                        <div className="text-xs text-gray-500">{house.mobile}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{house.block}</div>
                        <div className="text-xs text-gray-500">{house.panchayat}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(house.stage)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {house.inspections?.length || 0} inspections
                        </span>
                      </td>
                    </tr>
                    
                    {expandedHouses.has(house.h_id) && house.inspections && (
                      <tr>
                        <td colSpan="5" className="px-0 py-0">
                          <div className="bg-gray-50 border-l-4 border-blue-500">
                            <table className="min-w-full">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-600">Date</th>
                                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-600">Stage</th>
                                  <th className="px-6 py-2 text-left text-xs font-medium text-gray-600">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {house.inspections.map((inspection) => (
                                  <tr 
                                    key={inspection.id}
                                    className="hover:bg-white cursor-pointer border-b border-gray-200"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      fetchInspectionDetails(inspection.id);
                                    }}
                                  >
                                    <td className="px-6 py-3 text-sm text-gray-900">{formatDate(inspection.at)}</td>
                                    <td className="px-6 py-3">{getStatusBadge(inspection.stage)}</td>
                                    <td className="px-6 py-3">
                                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                                        {detailsLoading ? 'Loading...' : 'View Details →'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                    {searchQuery ? `No houses found matching "${searchQuery}"` : 'No houses found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InspectionList;